import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminHttpClient } from '../../api/client.js';
import { SPEECH_PROTOCOLS } from '../../../../shared-types/src/realtime.js';
import type { CreditRateSettingsView, PutCreditRateSettingsRequest, RealtimeVoiceProtocol, SpeechProtocol } from '../../../../shared-types/src/index.js';

const LIVE_TOKEN_PRICES = ['audioInputUsdPerMillion', 'audioOutputUsdPerMillion', 'textInputUsdPerMillion', 'textOutputUsdPerMillion'];

/** Realtime prices land in `voiceRates`, speech in `speechRates`; the server carries every other model's rate forward. */
function rateShape(protocol: RealtimeVoiceProtocol | SpeechProtocol) {
  if (protocol === 'openai-live' || protocol === 'gemini-live') {
    return protocol === 'openai-live'
      ? { list: 'voiceRates' as const, unit: 'session_duration' as const, required: ['usdPerMinute'], optional: [] }
      : { list: 'voiceRates' as const, unit: 'token_breakdown' as const, required: LIVE_TOKEN_PRICES,
        optional: ['cachedAudioInputUsdPerMillion', 'cachedTextInputUsdPerMillion'] };
  }
  const binding = SPEECH_PROTOCOLS[protocol];
  // A speech token rate prices every modality the provider reports; cache prices stay optional.
  return binding.unit === 'audio_duration'
    ? { list: 'speechRates' as const, unit: binding.unit, required: ['usdPerMinute'], optional: [] }
    : { list: 'speechRates' as const, unit: binding.unit, required: binding.pricedTokens.map(field => field.replace(/Tokens$/, 'UsdPerMillion')),
      optional: ['cachedAudioInputUsdPerMillion', 'cachedTextInputUsdPerMillion'] };
}

export function VoiceRateEditor({ http, modelId, protocol }: { http: AdminHttpClient; modelId: string; protocol: RealtimeVoiceProtocol | SpeechProtocol }) {
  const { t } = useTranslation();
  const shape = rateShape(protocol);
  const [loaded, setLoaded] = useState<CreditRateSettingsView>();
  const [values, setValues] = useState<Record<string, string>>({ marginMultiplier: '1' });
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    let active = true;
    void http.getRateSettings().then(view => {
      if (!active) return;
      setLoaded(view);
      const rate = view[shape.list]?.find(row => row.catalogModelId === modelId);
      if (rate) setValues(Object.fromEntries(Object.entries(rate).map(([key, value]) => [key, String(value)])));
    }).catch(() => { if (active) setError(true); }).finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [http, modelId, shape.list]);
  const perMinute = shape.unit !== 'token_breakdown';
  const ready = loaded && [...shape.required, 'marginMultiplier', 'sourceUrl', 'verifiedAt'].every(key => values[key]?.trim());
  const sampleUsd = shape.required.reduce((sum, key) => sum + Number(values[key] ?? 0), 0) * Number(values.marginMultiplier ?? 1) / (perMinute ? 1 : 1000);
  async function save() {
    if (!ready || busy || !loaded) return;
    setBusy(true); setError(false); setSaved(false);
    const prices = Object.fromEntries([...shape.required, ...shape.optional].filter(key => values[key]?.trim()).map(key => [key, Number(values[key])]));
    const rate = { catalogModelId: modelId, marginMultiplier: Number(values.marginMultiplier), sourceUrl: values.sourceUrl!, verifiedAt: values.verifiedAt!, unit: shape.unit, ...prices };
    const input = { expectedPublishedVersion: loaded.publishedVersion ?? '', [shape.list]: [rate] } as PutCreditRateSettingsRequest;
    try {
      const next = await http.putRateSettings(input);
      setLoaded(next); setSaved(true);
    } catch { setError(true); }
    finally { setBusy(false); }
  }
  return <section className="form-grid realtime-voice-rate" aria-label={t('voiceRates.title')}>
    <h4>{t('voiceRates.title')}</h4>
    <p>{t('voiceRates.historyNotice')}</p>
    <fieldset className="form-grid" disabled={busy}>
      {[...shape.required, ...shape.optional, 'marginMultiplier', 'sourceUrl', 'verifiedAt'].map(key => <label key={key}>
        {t(`voiceRates.${key}`)}
        <input type={key === 'sourceUrl' ? 'url' : key === 'verifiedAt' ? 'text' : 'number'} value={values[key] ?? ''}
          {...(key === 'sourceUrl' || key === 'verifiedAt' ? {} : { min: 0, step: 'any' })}
          onChange={event => { setSaved(false); setValues({ ...values, [key]: event.target.value }); }} />
      </label>)}
      <p>{t(perMinute ? 'voiceRates.previewMinute' : 'voiceRates.previewTokens', { usd: shape.required.every(key => values[key]?.trim()) && Number.isFinite(sampleUsd) ? sampleUsd.toFixed(6) : '—' })}</p>
      <button type="button" disabled={!ready || busy} onClick={() => void save()}>{t('voiceRates.publish')}</button>
    </fieldset>
    {saved ? <p role="status">{t('voiceRates.saved')}</p> : null}
    {error ? <p role="alert">{t('voiceRates.failed')}</p> : null}
  </section>;
}
